import { useEffect } from 'react'
import { useForm, Controller, useFieldArray, useWatch, type Control, type FieldErrors, type UseFormSetValue, type UseFormGetValues } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Stack, TextField,
  Alert, CircularProgress, IconButton, Autocomplete, Box, FormControlLabel, Checkbox, Chip, Typography,
} from '@mui/material'
import { Add, DeleteOutlined } from '@mui/icons-material'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  createSupplierInvoice, updateSupplierInvoice, getSupplierInvoice,
  type SupplierInvoiceListItem, type SaveSupplierInvoicePayload, type InvoiceLineMatchStatus,
} from '../../api/facturiFurnizor'
import { listSuppliers } from '../../api/furnizori'
import { listPurchaseOrders, getPurchaseOrder, getSuggestedPrice, type PurchaseOrderDetail } from '../../api/comenziFurnizor'
import { listGoodsReceipts } from '../../api/receptii'
import { listArticles, listUnits, type ArticleListItem, type UnitOfMeasure } from '../../api/nomenclatoare'
import { listVatRates, type VatRate } from '../../api/taxe'
import { errorMessage } from '../../api/client'

const MATCH_LABEL: Record<InvoiceLineMatchStatus, string> = {
  Match: 'OK', PriceException: 'Preț diferit', QuantityException: 'Cantitate depășită', NoReference: 'Fără referință',
}
const MATCH_COLOR: Record<InvoiceLineMatchStatus, 'success' | 'warning' | 'default'> = {
  Match: 'success', PriceException: 'warning', QuantityException: 'warning', NoReference: 'default',
}
const SOURCE_LABEL: Record<string, string> = { 'Preț contract': 'contract', 'Preț negociat furnizor': 'preț negociat' }

const lineSchema = z.object({
  supplierArticleCode: z.string().optional(),
  articleId: z.string().nullable(),
  description: z.string().min(1, 'Câmp obligatoriu'),
  unitOfMeasureId: z.string().nullable(),
  quantity: z.string().min(1, 'Câmp obligatoriu'),
  unitPrice: z.string().min(1, 'Câmp obligatoriu'),
  vatRateId: z.string().nullable(),
})
const schema = z.object({
  number: z.string().min(1, 'Câmp obligatoriu'),
  issueDate: z.string().min(1, 'Câmp obligatoriu'),
  receivedDate: z.string().min(1, 'Câmp obligatoriu'),
  dueDate: z.string().min(1, 'Câmp obligatoriu'),
  supplierId: z.string().min(1, 'Selectați un furnizor'),
  purchaseOrderId: z.string().nullable(),
  goodsReceiptId: z.string().nullable(),
  isServiceInvoiceWithoutPO: z.boolean(),
  serviceInvoiceApprovalReason: z.string().optional(),
  currency: z.string().min(1, 'Câmp obligatoriu'),
  lines: z.array(lineSchema).min(1, 'Adăugați cel puțin o linie'),
})
type FormValues = z.infer<typeof schema>

const EMPTY: FormValues = {
  number: '', issueDate: new Date().toISOString().slice(0, 10), receivedDate: new Date().toISOString().slice(0, 10),
  dueDate: '', supplierId: '', purchaseOrderId: null, goodsReceiptId: null, isServiceInvoiceWithoutPO: false,
  serviceInvoiceApprovalReason: '', currency: 'RON', lines: [],
}

function LineRow({
  index, control, errors, articles, units, vatRates, supplierId, poDetail, matchStatus, setValue, getValues, onRemove,
}: {
  index: number
  control: Control<FormValues>
  errors: FieldErrors<FormValues>
  articles: ArticleListItem[] | undefined
  units: UnitOfMeasure[] | undefined
  vatRates: VatRate[] | undefined
  supplierId: string
  poDetail: PurchaseOrderDetail | undefined
  matchStatus: InvoiceLineMatchStatus | undefined
  setValue: UseFormSetValue<FormValues>
  getValues: UseFormGetValues<FormValues>
  onRemove: () => void
}) {
  const articleId = useWatch({ control, name: `lines.${index}.articleId` })
  const matchedOrderLine = articleId ? poDetail?.lines.find((l) => l.articleId === articleId) : undefined

  const { data: suggested } = useQuery({
    queryKey: ['achizitii', 'comenzi', 'pret-sugerat', supplierId, articleId, null],
    queryFn: () => getSuggestedPrice(supplierId, articleId!, null),
    enabled: !!supplierId && !!articleId && !matchedOrderLine,
  })

  const isBlankPrice = () => {
    const current = getValues(`lines.${index}.unitPrice`)
    return !current || Number(current) === 0
  }

  useEffect(() => {
    if (!articleId) return
    if (matchedOrderLine) {
      if (isBlankPrice()) setValue(`lines.${index}.unitPrice`, String(matchedOrderLine.unitPrice))
      if (!getValues(`lines.${index}.vatRateId`)) setValue(`lines.${index}.vatRateId`, matchedOrderLine.vatRateId)
      if (!getValues(`lines.${index}.unitOfMeasureId`)) setValue(`lines.${index}.unitOfMeasureId`, matchedOrderLine.unitOfMeasureId)
      if (!getValues(`lines.${index}.description`)) setValue(`lines.${index}.description`, matchedOrderLine.articleDescription)
      return
    }
    const article = articles?.find((a) => a.id === articleId)
    if (article?.vatRateId && !getValues(`lines.${index}.vatRateId`)) setValue(`lines.${index}.vatRateId`, article.vatRateId)
    if (article && !getValues(`lines.${index}.description`)) setValue(`lines.${index}.description`, article.shortDescription)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [articleId, matchedOrderLine, articles, index, getValues, setValue])

  useEffect(() => {
    if (!matchedOrderLine && suggested?.price != null && isBlankPrice()) {
      setValue(`lines.${index}.unitPrice`, String(suggested.price))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [suggested, matchedOrderLine, index, getValues, setValue])

  const sourceLabel = matchedOrderLine
    ? `Din comanda ${poDetail?.number}`
    : suggested?.price != null && SOURCE_LABEL[suggested.source]
      ? `Sugerat din ${SOURCE_LABEL[suggested.source]}`
      : null

  return (
    <Stack direction="row" spacing={1.5} sx={{ alignItems: 'flex-start' }}>
      <Controller name={`lines.${index}.articleId`} control={control} render={({ field }) => (
        <Autocomplete
          sx={{ width: 220 }} options={articles?.map((a) => a.id) ?? []}
          getOptionLabel={(id) => { const a = articles?.find((x) => x.id === id); return a ? `${a.code} — ${a.shortDescription}` : id }}
          value={field.value} onChange={(_, v) => field.onChange(v)}
          renderInput={(params) => <TextField {...params} label="Articol (opțional)" />}
        />
      )} />
      <Controller name={`lines.${index}.description`} control={control} render={({ field }) => (
        <TextField {...field} label="Descriere" sx={{ flexGrow: 1 }} error={!!errors.lines?.[index]?.description} helperText={errors.lines?.[index]?.description?.message} />
      )} />
      <Controller name={`lines.${index}.quantity`} control={control} render={({ field }) => (
        <TextField {...field} type="number" label="Cantitate" sx={{ width: 110 }} error={!!errors.lines?.[index]?.quantity} helperText={errors.lines?.[index]?.quantity?.message} />
      )} />
      <Controller name={`lines.${index}.unitOfMeasureId`} control={control} render={({ field }) => (
        <Autocomplete
          sx={{ width: 100 }} options={units?.map((u) => u.id) ?? []}
          getOptionLabel={(id) => units?.find((u) => u.id === id)?.code ?? id}
          value={field.value} onChange={(_, v) => field.onChange(v)}
          renderInput={(params) => <TextField {...params} label="UM" />}
        />
      )} />
      <Box sx={{ width: 120 }}>
        <Controller name={`lines.${index}.unitPrice`} control={control} render={({ field }) => (
          <TextField {...field} type="number" label="Preț unitar" fullWidth error={!!errors.lines?.[index]?.unitPrice} helperText={errors.lines?.[index]?.unitPrice?.message} />
        )} />
        {sourceLabel && <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>{sourceLabel}</Typography>}
      </Box>
      <Controller name={`lines.${index}.vatRateId`} control={control} render={({ field }) => (
        <Autocomplete
          sx={{ width: 130 }} options={vatRates?.map((v) => v.id) ?? []}
          getOptionLabel={(id) => { const v = vatRates?.find((x) => x.id === id); return v ? `${v.ratePercent}%` : id }}
          value={field.value} onChange={(_, v) => field.onChange(v)}
          renderInput={(params) => <TextField {...params} label="TVA" />}
        />
      )} />
      {matchStatus && <Chip size="small" sx={{ mt: 1 }} label={MATCH_LABEL[matchStatus]} color={MATCH_COLOR[matchStatus]} />}
      <IconButton onClick={onRemove} sx={{ mt: 0.5 }}><DeleteOutlined fontSize="small" /></IconButton>
    </Stack>
  )
}

export default function SupplierInvoiceFormDialog({ target, onClose }: { target: SupplierInvoiceListItem | null | 'new'; onClose: () => void }) {
  const open = target !== null
  const editing = target !== null && target !== 'new'
  const qc = useQueryClient()

  const { data: suppliers } = useQuery({ queryKey: ['achizitii', 'furnizori', 'toti'], queryFn: () => listSuppliers(), enabled: open })
  const { data: orders } = useQuery({ queryKey: ['achizitii', 'comenzi'], queryFn: listPurchaseOrders, enabled: open })
  const { data: receipts } = useQuery({ queryKey: ['achizitii', 'receptii'], queryFn: listGoodsReceipts, enabled: open })
  const { data: articles } = useQuery({ queryKey: ['nomenclatoare', 'articole', 'toate'], queryFn: () => listArticles(), enabled: open })
  const { data: units } = useQuery({ queryKey: ['nomenclatoare', 'um'], queryFn: listUnits, enabled: open })
  const { data: vatRates } = useQuery({ queryKey: ['financiar', 'taxe', 'cote-tva'], queryFn: listVatRates, enabled: open })
  const { data: detail } = useQuery({
    queryKey: ['achizitii', 'facturi', editing ? (target as SupplierInvoiceListItem).id : null],
    queryFn: () => getSupplierInvoice((target as SupplierInvoiceListItem).id),
    enabled: editing,
  })

  const { control, handleSubmit, reset, setValue, getValues, formState: { errors } } = useForm<FormValues>({ resolver: zodResolver(schema), defaultValues: EMPTY })
  const lines = useFieldArray({ control, name: 'lines' })
  const supplierId = useWatch({ control, name: 'supplierId' })
  const purchaseOrderId = useWatch({ control, name: 'purchaseOrderId' })
  const isServiceInvoiceWithoutPO = useWatch({ control, name: 'isServiceInvoiceWithoutPO' })

  const { data: poDetail } = useQuery({
    queryKey: ['achizitii', 'comenzi', purchaseOrderId],
    queryFn: () => getPurchaseOrder(purchaseOrderId!),
    enabled: !!purchaseOrderId,
  })

  useEffect(() => {
    if (editing && detail) {
      reset({
        number: detail.number, issueDate: detail.issueDate, receivedDate: detail.receivedDate, dueDate: detail.dueDate,
        supplierId: detail.supplierId, purchaseOrderId: detail.purchaseOrderId, goodsReceiptId: detail.goodsReceiptId,
        isServiceInvoiceWithoutPO: detail.isServiceInvoiceWithoutPO, serviceInvoiceApprovalReason: detail.serviceInvoiceApprovalReason ?? '',
        currency: detail.currency,
        lines: detail.lines.map((l) => ({
          supplierArticleCode: l.supplierArticleCode ?? '', articleId: l.articleId, description: l.description,
          unitOfMeasureId: l.unitOfMeasureId, quantity: String(l.quantity), unitPrice: String(l.unitPrice), vatRateId: l.vatRateId,
        })),
      })
    } else if (target === 'new') {
      reset(EMPTY)
    }
  }, [target, editing, detail, reset])

  const mutation = useMutation({
    mutationFn: async (values: FormValues) => {
      const payload: SaveSupplierInvoicePayload = {
        number: values.number, issueDate: values.issueDate, receivedDate: values.receivedDate, dueDate: values.dueDate,
        supplierId: values.supplierId, purchaseOrderId: values.isServiceInvoiceWithoutPO ? null : values.purchaseOrderId,
        goodsReceiptId: values.goodsReceiptId, isServiceInvoiceWithoutPO: values.isServiceInvoiceWithoutPO,
        serviceInvoiceApprovalReason: values.serviceInvoiceApprovalReason || null, currency: values.currency,
        lines: values.lines.map((l) => ({
          supplierArticleCode: l.supplierArticleCode || null, articleId: l.articleId, description: l.description,
          unitOfMeasureId: l.unitOfMeasureId, quantity: Number(l.quantity), unitPrice: Number(l.unitPrice), vatRateId: l.vatRateId,
        })),
      }
      if (editing) return updateSupplierInvoice((target as SupplierInvoiceListItem).id, payload)
      return createSupplierInvoice(payload)
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['achizitii', 'facturi'] })
      onClose()
    },
  })

  const supplierOrders = (orders ?? []).filter((o) => o.supplierId === supplierId)
  const orderReceipts = (receipts ?? []).filter((r) => r.purchaseOrderId === purchaseOrderId)

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <form onSubmit={handleSubmit((v) => mutation.mutate(v))}>
        <DialogTitle>{editing ? 'Editare factură furnizor' : 'Factură furnizor nouă'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 0.5 }}>
            <Stack direction="row" spacing={2}>
              <Controller name="number" control={control} render={({ field }) => (
                <TextField {...field} label="Nr. factură (al furnizorului)" sx={{ flexGrow: 1 }} error={!!errors.number} helperText={errors.number?.message} />
              )} />
              <Controller name="supplierId" control={control} render={({ field }) => (
                <Autocomplete
                  sx={{ flexGrow: 1 }} options={suppliers?.map((s) => s.id) ?? []}
                  getOptionLabel={(id) => suppliers?.find((s) => s.id === id)?.name ?? id}
                  value={field.value || null} onChange={(_, v) => field.onChange(v ?? '')}
                  renderInput={(params) => <TextField {...params} label="Furnizor" error={!!errors.supplierId} helperText={errors.supplierId?.message} />}
                />
              )} />
              <Controller name="currency" control={control} render={({ field }) => (
                <TextField {...field} label="Monedă" sx={{ width: 100 }} error={!!errors.currency} helperText={errors.currency?.message} />
              )} />
            </Stack>
            <Stack direction="row" spacing={2}>
              <Controller name="issueDate" control={control} render={({ field }) => (
                <TextField {...field} type="date" label="Data emiterii" sx={{ flexGrow: 1 }} slotProps={{ inputLabel: { shrink: true } }}
                  error={!!errors.issueDate} helperText={errors.issueDate?.message} />
              )} />
              <Controller name="receivedDate" control={control} render={({ field }) => (
                <TextField {...field} type="date" label="Data primirii" sx={{ flexGrow: 1 }} slotProps={{ inputLabel: { shrink: true } }}
                  error={!!errors.receivedDate} helperText={errors.receivedDate?.message} />
              )} />
              <Controller name="dueDate" control={control} render={({ field }) => (
                <TextField {...field} type="date" label="Scadență" sx={{ flexGrow: 1 }} slotProps={{ inputLabel: { shrink: true } }}
                  error={!!errors.dueDate} helperText={errors.dueDate?.message} />
              )} />
            </Stack>

            <Controller name="isServiceInvoiceWithoutPO" control={control} render={({ field }) => (
              <FormControlLabel control={<Checkbox checked={field.value} onChange={(e) => field.onChange(e.target.checked)} />}
                label="Factură de servicii/utilități, fără comandă (FR-AFF-006)" />
            )} />

            {!isServiceInvoiceWithoutPO ? (
              <Stack direction="row" spacing={2}>
                <Controller name="purchaseOrderId" control={control} render={({ field }) => (
                  <Autocomplete
                    sx={{ flexGrow: 1 }} options={supplierOrders.map((o) => o.id)}
                    getOptionLabel={(id) => supplierOrders.find((o) => o.id === id)?.number ?? id}
                    value={field.value} onChange={(_, v) => field.onChange(v)}
                    renderInput={(params) => <TextField {...params} label="Comandă furnizor (pentru 3-way match)" />}
                  />
                )} />
                <Controller name="goodsReceiptId" control={control} render={({ field }) => (
                  <Autocomplete
                    sx={{ flexGrow: 1 }} options={orderReceipts.map((r) => r.id)}
                    getOptionLabel={(id) => orderReceipts.find((r) => r.id === id)?.number ?? id}
                    value={field.value} onChange={(_, v) => field.onChange(v)}
                    renderInput={(params) => <TextField {...params} label="Recepție (opțional)" />}
                  />
                )} />
              </Stack>
            ) : (
              <Controller name="serviceInvoiceApprovalReason" control={control} render={({ field }) => (
                <TextField {...field} label="Motiv aprobare specială" fullWidth />
              )} />
            )}

            {typeof errors.lines?.message === 'string' && <Alert severity="warning">{errors.lines.message}</Alert>}
            {lines.fields.map((f, i) => (
              <LineRow
                key={f.id} index={i} control={control} errors={errors} articles={articles} units={units} vatRates={vatRates}
                supplierId={supplierId} poDetail={isServiceInvoiceWithoutPO ? undefined : poDetail}
                matchStatus={editing ? detail?.lines[i]?.matchStatus : undefined}
                setValue={setValue} getValues={getValues} onRemove={() => lines.remove(i)}
              />
            ))}
            <Box>
              <Button size="small" startIcon={<Add />}
                onClick={() => lines.append({ supplierArticleCode: '', articleId: null, description: '', unitOfMeasureId: null, quantity: '', unitPrice: '', vatRateId: null })}>
                Adaugă linie
              </Button>
            </Box>

            {Object.keys(errors).length > 0 && (
              <Alert severity="warning">Există câmpuri obligatorii necompletate — verificați câmpurile marcate cu roșu mai sus.</Alert>
            )}
            {mutation.isError && <Alert severity="error">{errorMessage(mutation.error)}</Alert>}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={onClose}>Renunță</Button>
          <Button type="submit" variant="contained" disabled={mutation.isPending}
            startIcon={mutation.isPending ? <CircularProgress size={16} color="inherit" /> : undefined}>
            Salvează
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  )
}
